// Manager/Admin analytics dashboard — the default landing screen for Manager and
// Admin roles. A tabbed, visually rich surface (Overview / Inspections / Compliance
// / Live) built on the shared SVG chart primitives in ./dashboard/charts. It polls
// the summary + live endpoints every 20s, lazy-loads compliance on first open, and
// embeds the existing live map (InspectionsDashboard) in the Live tab.
//
// Routing, nav placement, i18n key registration and the changelog entry are owned
// by the integrator — this file does not touch them.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  ClipboardCheck,
  Droplets,
  MapPin,
  RefreshCw,
  Users,
  AlertTriangle,
  Clock,
  CheckCircle2,
  TrendingUp,
  Activity,
  X,
  Bug,
  Sparkles,
} from 'lucide-react';
import { apiClient, ApiError } from '../lib/apiClient';
import i18n from '../i18n/config';
import {
  Donut,
  Legend,
  BarList,
  GroupedBarChart,
  StatTile,
  Pill,
  Tooltip,
  Card,
  Tabs,
  type DonutSegment,
  type BarDatum,
  type TrendSeries,
  type TabDef,
} from './dashboard/charts';
import {
  evaluatePropertyCompliance,
  type ComplianceStatus,
  type WaterRestrictionSchedule,
  type WaterRule,
  type CityScheduleEntry,
} from '../lib/waterRestrictions';
import { WaterRestrictionCityDrawer } from './WaterRestrictionCityDrawer';
import { InspectionsDashboard } from './InspectionsDashboard';

const POLL_INTERVAL_MS = 20_000;

// ---------------------------------------------------------------------------
// Endpoint response shapes
// ---------------------------------------------------------------------------

interface DashboardSummary {
  generated_at: string;
  reports: {
    total: number;
    drafts: number;
    pending_review: number;
    approved: number;
    rejected: number;
    today: number;
    this_week: number;
    this_month: number;
    by_status: Array<{ status_type: string; label: string; color: string; count: number }>;
  };
  trends: { weekly: Array<{ week_start: string; created: number; approved: number }> };
  technicians: Array<{
    user_id: string | null;
    display_name: string | null;
    email: string | null;
    total: number;
    submitted: number;
    approved: number;
  }>;
  issues: {
    total_issues_30d: number;
    controller_failures_30d: number;
    properties_with_issues_30d: number;
  };
  feedback: { open_bugs: number; open_features: number };
}

interface ActiveTech {
  user_id: string;
  display_name: string | null;
  user_email: string;
  job_site_name: string | null;
  status_label: string | null;
  status_color: string | null;
  last_activity: string;
  latitude: number | null;
  longitude: number | null;
}

interface ActivityFeedItem {
  id: string;
  created_at: string;
  category: string;
  message: string;
  user_email: string;
  display_name: string | null;
  job_site_name: string | null;
}

interface LiveResponse {
  technicians: ActiveTech[];
  feed: ActivityFeedItem[];
}

interface CityPropertyRow {
  id: string;
  name: string;
  address: string | null;
  water_restriction_class: 'residential' | 'non_residential' | null;
  water_restriction_override: unknown;
  controllers: Array<{
    id: string;
    controller_number: number;
    description: string | null;
    location: string | null;
    model: string | null;
    water_start_times: unknown;
    water_restriction_override: { acknowledged?: boolean } | null;
  }>;
}

interface ComplianceCity {
  city: string;
  schedule: WaterRestrictionSchedule | null;
  properties: CityPropertyRow[];
}

interface ComplianceResponse {
  cities: ComplianceCity[];
}

type TabKey = 'overview' | 'inspections' | 'compliance' | 'live';

type TechRow = DashboardSummary['technicians'][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(i18n.language || 'en', { numeric: 'auto' });
  if (Math.abs(diffSec) < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return rtf.format(-diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  return rtf.format(-diffDay, 'day');
}

/** Format a 'YYYY-MM-DD' week_start as 'M/D' for the trend x-axis. */
function weekLabel(weekStart: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekStart);
  if (!m) return weekStart;
  return `${Number(m[2])}/${Number(m[3])}`;
}

function techName(t: { display_name: string | null; email?: string | null; user_email?: string }): string {
  return t.display_name || t.email || t.user_email || '—';
}

const COMPLIANCE_META: Record<ComplianceStatus, { color: string; labelKey: string; labelEn: string }> = {
  compliant: { color: '#059669', labelKey: 'mgrDashboard.complianceCompliant', labelEn: 'Compliant' },
  non_compliant: { color: '#dc2626', labelKey: 'mgrDashboard.complianceNonCompliant', labelEn: 'Non-compliant' },
  undetermined: { color: '#f59e0b', labelKey: 'mgrDashboard.complianceUndetermined', labelEn: "Can't verify" },
  no_schedule: { color: '#9ca3af', labelKey: 'mgrDashboard.complianceNoSchedule', labelEn: 'No schedule' },
};
const COMPLIANCE_ORDER: ComplianceStatus[] = ['compliant', 'non_compliant', 'undetermined', 'no_schedule'];

// Friendly labels for the activity feed (mirrors InspectionsDashboard mapping).
const CATEGORY_LABEL_KEY: Record<string, { key: string; en: string }> = {
  inspection_workflow_opened: { key: 'mgrDashboard.evt.opened', en: 'Opened an inspection' },
  inspection_save_started: { key: 'mgrDashboard.evt.saving', en: 'Saving…' },
  inspection_save_succeeded: { key: 'mgrDashboard.evt.saved', en: 'Saved progress' },
  inspection_save_failed: { key: 'mgrDashboard.evt.saveFailed', en: 'Save failed' },
  inspection_save_loop_blocked: { key: 'mgrDashboard.evt.saveLoopBlocked', en: 'Autosave stopped (stuck)' },
  inspection_submit_blocked: { key: 'mgrDashboard.evt.submitBlocked', en: 'Submit blocked' },
  inspection_tab_changed: { key: 'mgrDashboard.evt.tabChanged', en: 'Switched section' },
  inspection_location_heartbeat: { key: 'mgrDashboard.evt.location', en: 'Location update' },
  inspection_location_left_property: { key: 'mgrDashboard.evt.leftProperty', en: 'Left the property' },
  gps_capture_started: { key: 'mgrDashboard.evt.gpsStarted', en: 'GPS capture started' },
  gps_capture_succeeded: { key: 'mgrDashboard.evt.gpsSucceeded', en: 'GPS captured' },
  gps_capture_retry: { key: 'mgrDashboard.evt.gpsRetry', en: 'Retrying GPS' },
  gps_capture_failed: { key: 'mgrDashboard.evt.gpsFailed', en: 'GPS capture failed' },
  gps_capture_unavailable: { key: 'mgrDashboard.evt.gpsUnavailable', en: 'GPS unavailable' },
};

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Small inline error banner used inside cards.
// ---------------------------------------------------------------------------
function InlineError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ManagerDashboard() {
  const { t } = useTranslation('common');
  const [tab, setTab] = useState<TabKey>('overview');

  // Summary + live (polled).
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [live, setLive] = useState<LiveResponse>({ technicians: [], feed: [] });
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Compliance (lazy, manual refresh).
  const [compliance, setCompliance] = useState<ComplianceResponse | null>(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const complianceRequested = useRef(false);

  // Drill-downs.
  const [techDetail, setTechDetail] = useState<TechRow | null>(null);
  const [drawerEntry, setDrawerEntry] = useState<CityScheduleEntry | null>(null);

  // Poll summary + live every 20s.
  const pollAll = useCallback(async () => {
    const summaryP = apiClient
      .get<DashboardSummary>('/api/dashboard/summary')
      .then((res) => ({ ok: true as const, res }))
      .catch((err) => ({ ok: false as const, err }));
    const liveP = apiClient
      .get<LiveResponse>('/api/dashboard/live')
      .then((res) => ({ ok: true as const, res }))
      .catch((err) => ({ ok: false as const, err }));
    const [s, l] = await Promise.all([summaryP, liveP]);
    return { s, l };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const { s, l } = await pollAll();
      if (cancelled) return;
      if (s.ok) {
        setSummary(s.res);
        setSummaryError(null);
      } else {
        setSummaryError(errMessage(s.err, t('mgrDashboard.loadError', 'Failed to load dashboard data.')));
      }
      if (l.ok) {
        setLive(l.res);
        setLiveError(null);
      } else {
        setLiveError(errMessage(l.err, t('mgrDashboard.loadError', 'Failed to load dashboard data.')));
      }
      setLastUpdated(new Date());
      setLoading(false);
    };
    void run();
    const id = setInterval(run, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollAll, t]);

  // Lazy-load compliance the first time the Compliance tab opens (also exposed
  // as a manual refresh below).
  const loadCompliance = useCallback(async () => {
    setComplianceLoading(true);
    setComplianceError(null);
    try {
      const res = await apiClient.get<ComplianceResponse>('/api/dashboard/compliance');
      setCompliance(res);
    } catch (err) {
      setComplianceError(errMessage(err, t('mgrDashboard.complianceLoadError', 'Failed to load compliance data.')));
    } finally {
      setComplianceLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (tab === 'compliance' && !complianceRequested.current) {
      complianceRequested.current = true;
      void loadCompliance();
    }
  }, [tab, loadCompliance]);

  // Org-wide compliance rollup (computed once compliance is loaded).
  const complianceRollup = useMemo(() => {
    if (!compliance) return null;
    const counts: Record<ComplianceStatus, number> = {
      compliant: 0,
      non_compliant: 0,
      undetermined: 0,
      no_schedule: 0,
    };
    const byCity: Array<{ city: string; flagged: number; total: number; entry: CityScheduleEntry }> = [];
    let evaluated = 0;
    for (const c of compliance.cities) {
      let flagged = 0;
      for (const p of c.properties) {
        const r = evaluatePropertyCompliance(c.schedule, {
          address: p.address,
          water_restriction_class: p.water_restriction_class,
          water_restriction_override: p.water_restriction_override as WaterRule | null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          controllers: p.controllers as any,
        });
        counts[r.status] += 1;
        if (r.status !== 'no_schedule') evaluated += 1;
        if (r.status === 'non_compliant' || r.status === 'undetermined') flagged += 1;
      }
      byCity.push({
        city: c.city,
        flagged,
        total: c.properties.length,
        entry: { city: c.city, property_count: c.properties.length, schedule: c.schedule },
      });
    }
    const total = counts.compliant + counts.non_compliant + counts.undetermined + counts.no_schedule;
    // Compliance % = compliant / (compliant + non_compliant + undetermined) — i.e.
    // among properties we could evaluate at all (a schedule + a programmed controller).
    const denom = counts.compliant + counts.non_compliant + counts.undetermined;
    const pct = denom > 0 ? Math.round((counts.compliant / denom) * 100) : null;
    return { counts, byCity, total, evaluated, pct };
  }, [compliance]);

  const reports = summary?.reports;
  const activeCount = live.technicians.length;

  const tabs: TabDef[] = [
    {
      key: 'overview',
      label: t('mgrDashboard.tabOverview', 'Overview'),
      icon: <LayoutDashboard size={15} />,
    },
    {
      key: 'inspections',
      label: t('mgrDashboard.tabInspections', 'Inspections'),
      icon: <ClipboardCheck size={15} />,
      badge: reports && reports.pending_review > 0 ? reports.pending_review : undefined,
    },
    {
      key: 'compliance',
      label: t('mgrDashboard.tabCompliance', 'Compliance'),
      icon: <Droplets size={15} />,
    },
    {
      key: 'live',
      label: t('mgrDashboard.tabLive', 'Live'),
      icon: <MapPin size={15} />,
      badge: activeCount > 0 ? activeCount : undefined,
    },
  ];

  const refresh = () => {
    void pollAll().then(({ s, l }) => {
      if (s.ok) {
        setSummary(s.res);
        setSummaryError(null);
      }
      if (l.ok) {
        setLive(l.res);
        setLiveError(null);
      }
      setLastUpdated(new Date());
    });
    if (tab === 'compliance') void loadCompliance();
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-theme-primary">
            <LayoutDashboard size={24} />
            {t('mgrDashboard.title', 'Dashboard')}
          </h1>
          {lastUpdated && (
            <span className="inline-flex items-center gap-1 text-xs text-theme-secondary">
              <Clock size={12} />
              {t('mgrDashboard.updated', 'Updated {{time}}', { time: relativeTime(lastUpdated.toISOString()) })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            title={t('mgrDashboard.refresh', 'Refresh')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-theme-secondary shadow-sm transition-colors hover:bg-subtle"
          >
            <RefreshCw size={15} className={loading || complianceLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{t('mgrDashboard.refresh', 'Refresh')}</span>
          </button>
          <Tabs tabs={tabs} active={tab} onChange={(k) => setTab(k as TabKey)} />
        </div>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-theme-secondary">
          <RefreshCw size={18} className="animate-spin" />
          {t('mgrDashboard.loading', 'Loading dashboard…')}
        </div>
      ) : (
        <>
          {tab === 'overview' && (
            <OverviewTab
              summary={summary}
              summaryError={summaryError}
              live={live}
              liveError={liveError}
              loading={loading}
              compliancePct={complianceRollup?.pct ?? null}
              onPendingClick={() => setTab('inspections')}
            />
          )}
          {tab === 'inspections' && (
            <InspectionsTab
              summary={summary}
              summaryError={summaryError}
              onTechClick={(tech) => setTechDetail(tech)}
            />
          )}
          {tab === 'compliance' && (
            <ComplianceTab
              rollup={complianceRollup}
              loading={complianceLoading}
              error={complianceError}
              onCityClick={(entry) => setDrawerEntry(entry)}
            />
          )}
          {tab === 'live' && (
            <div className="-mt-1">
              <InspectionsDashboard />
            </div>
          )}
        </>
      )}

      {/* Technician drill-down modal */}
      {techDetail && <TechModal tech={techDetail} onClose={() => setTechDetail(null)} />}

      {/* City compliance drawer (reuses the existing component) */}
      {drawerEntry && <WaterRestrictionCityDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status donut helpers (shared by Overview + Inspections)
// ---------------------------------------------------------------------------

function statusSegments(reports: DashboardSummary['reports']): DonutSegment[] {
  return reports.by_status
    .map((s) => ({ key: s.status_type, label: s.label, value: s.count, color: s.color }))
    .filter((s) => s.value > 0);
}

function ReportStatusDonut({ reports }: { reports: DashboardSummary['reports'] }) {
  const { t } = useTranslation('common');
  const segments = statusSegments(reports);
  return (
    <div className="flex items-center gap-4">
      <Donut
        segments={segments}
        centerValue={reports.total}
        centerLabel={t('mgrDashboard.reportsLabel', 'reports')}
      />
      <div className="flex-1 min-w-0">
        {segments.length === 0 ? (
          <p className="text-sm text-theme-secondary">{t('mgrDashboard.noReports', 'No reports yet.')}</p>
        ) : (
          <Legend segments={segments} total={reports.total} />
        )}
      </div>
    </div>
  );
}

function WeeklyTrend({ weekly }: { weekly: DashboardSummary['trends']['weekly'] }) {
  const { t } = useTranslation('common');
  const categories = weekly.map((w) => weekLabel(w.week_start));
  const series: TrendSeries[] = [
    { key: 'created', label: t('mgrDashboard.trendCreated', 'Created'), color: '#3b82f6', points: weekly.map((w) => w.created) },
    { key: 'approved', label: t('mgrDashboard.trendApproved', 'Approved'), color: '#059669', points: weekly.map((w) => w.approved) },
  ];
  if (weekly.length === 0) {
    return <p className="py-6 text-center text-sm text-theme-secondary">{t('mgrDashboard.noTrend', 'No trend data yet.')}</p>;
  }
  return <GroupedBarChart series={series} categories={categories} />;
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({
  summary,
  summaryError,
  live,
  liveError,
  loading,
  compliancePct,
  onPendingClick,
}: {
  summary: DashboardSummary | null;
  summaryError: string | null;
  live: LiveResponse;
  liveError: string | null;
  loading: boolean;
  compliancePct: number | null;
  onPendingClick: () => void;
}) {
  const { t } = useTranslation('common');
  const reports = summary?.reports;

  return (
    <div className="flex flex-col gap-4">
      {summaryError && <InlineError message={summaryError} />}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile
          icon={<Users size={15} />}
          label={t('mgrDashboard.tileActiveNow', 'Active now')}
          value={live.technicians.length}
          tone="sky"
          sub={t('mgrDashboard.tileActiveNowSub', 'technicians')}
        />
        <StatTile
          icon={<Clock size={15} />}
          label={t('mgrDashboard.tilePendingReview', 'Pending review')}
          value={reports?.pending_review ?? '—'}
          tone="amber"
          onClick={onPendingClick}
          title={t('mgrDashboard.tilePendingReviewHint', 'View inspections')}
        />
        <StatTile
          icon={<ClipboardCheck size={15} />}
          label={t('mgrDashboard.tileThisMonth', 'This month')}
          value={reports?.this_month ?? '—'}
          tone="blue"
          sub={t('mgrDashboard.tileThisMonthSub', 'reports created')}
        />
        <StatTile
          icon={<CheckCircle2 size={15} />}
          label={t('mgrDashboard.tileApproved', 'Approved')}
          value={reports?.approved ?? '—'}
          tone="emerald"
        />
        <StatTile
          icon={<AlertTriangle size={15} />}
          label={t('mgrDashboard.tileOpenIssues', 'Open issues')}
          value={summary?.issues.total_issues_30d ?? '—'}
          tone="red"
          // It is one row per repair a technician flagged on a zone, not one per
          // zone or per property — so the number is legitimately in the
          // hundreds/thousands. Spell that out so it doesn't read as a backlog.
          sub={t('mgrDashboard.tileOpenIssuesSubItems', 'repair items · last 30 days')}
          title={t(
            'mgrDashboard.tileOpenIssuesHint',
            'Every individual repair a technician flagged on a zone in the last 30 days. One zone can need more than one repair, and each counts separately. Zones checked clean ("No Repairs Needed") are not counted.',
          )}
        />
        <StatTile
          icon={<Droplets size={15} />}
          label={t('mgrDashboard.tileCompliance', 'Compliance')}
          value={compliancePct == null ? '—' : `${compliancePct}%`}
          tone="violet"
          sub={compliancePct == null ? t('mgrDashboard.tileComplianceHint', 'open Compliance') : undefined}
        />
      </div>

      {/* Donut + feed + trend */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title={t('mgrDashboard.cardReportStatus', 'Report status')} icon={<ClipboardCheck size={16} />}>
          {reports ? <ReportStatusDonut reports={reports} /> : <p className="text-sm text-theme-secondary">—</p>}
        </Card>

        <Card
          title={t('mgrDashboard.cardActivity', 'Live activity')}
          icon={<Activity size={16} />}
          action={loading ? <RefreshCw size={13} className="animate-spin text-theme-secondary" /> : undefined}
          bodyClassName="p-0"
        >
          {liveError ? (
            <div className="p-4">
              <InlineError message={liveError} />
            </div>
          ) : (
            <ActivityFeedList feed={live.feed.slice(0, 8)} />
          )}
        </Card>

        <Card title={t('mgrDashboard.cardWeeklyTrend', 'Weekly trend')} icon={<TrendingUp size={16} />}>
          {summary ? <WeeklyTrend weekly={summary.trends.weekly} /> : <p className="text-sm text-theme-secondary">—</p>}
        </Card>
      </div>
    </div>
  );
}

function ActivityFeedList({ feed }: { feed: ActivityFeedItem[] }) {
  const { t } = useTranslation('common');
  const friendly = useCallback(
    (item: ActivityFeedItem) => {
      const mapped = CATEGORY_LABEL_KEY[item.category];
      if (mapped) return t(mapped.key, mapped.en);
      return item.message || item.category.replace(/_/g, ' ');
    },
    [t],
  );
  if (feed.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-theme-secondary">{t('mgrDashboard.noActivity', 'No recent activity.')}</p>;
  }
  return (
    <ul className="divide-y divide-line">
      {feed.map((item) => (
        <li key={item.id} className="px-4 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium text-theme-primary">
              {item.display_name || item.user_email}
            </span>
            <span className="flex-shrink-0 text-[11px] text-theme-secondary">{relativeTime(item.created_at)}</span>
          </div>
          <div className="truncate text-xs text-theme-secondary">
            {friendly(item)}
            {item.job_site_name ? ` · ${item.job_site_name}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Inspections tab
// ---------------------------------------------------------------------------

function InspectionsTab({
  summary,
  summaryError,
  onTechClick,
}: {
  summary: DashboardSummary | null;
  summaryError: string | null;
  onTechClick: (tech: TechRow) => void;
}) {
  const { t } = useTranslation('common');

  if (summaryError && !summary) return <InlineError message={summaryError} />;
  if (!summary) return <p className="py-12 text-center text-sm text-theme-secondary">—</p>;

  const { reports, technicians, issues } = summary;

  const leaderboard: BarDatum[] = technicians.map((tech) => ({
    key: tech.user_id ?? tech.email ?? techName(tech),
    label: techName(tech),
    value: tech.total,
    color: '#3b82f6',
    sub: t('mgrDashboard.techBarSub', '{{approved}} approved', { approved: tech.approved }),
  }));

  return (
    <div className="flex flex-col gap-4">
      {summaryError && <InlineError message={summaryError} />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title={t('mgrDashboard.cardReportStatus', 'Report status')} icon={<ClipboardCheck size={16} />}>
          <ReportStatusDonut reports={reports} />
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line pt-3">
            <Pill tone="gray">{t('mgrDashboard.pillToday', 'Today: {{n}}', { n: reports.today })}</Pill>
            <Pill tone="gray">{t('mgrDashboard.pillWeek', 'This week: {{n}}', { n: reports.this_week })}</Pill>
            <Pill tone="gray">{t('mgrDashboard.pillMonth', 'This month: {{n}}', { n: reports.this_month })}</Pill>
            {reports.drafts > 0 && <Pill tone="amber">{t('mgrDashboard.pillDrafts', '{{n}} drafts', { n: reports.drafts })}</Pill>}
          </div>
        </Card>

        <Card
          title={t('mgrDashboard.cardWeeklyTrend', 'Weekly trend')}
          icon={<TrendingUp size={16} />}
          className="lg:col-span-2"
        >
          <WeeklyTrend weekly={summary.trends.weekly} />
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card
          title={t('mgrDashboard.cardLeaderboard', 'Technician leaderboard')}
          icon={<Users size={16} />}
          className="lg:col-span-2"
          action={<span className="text-xs text-theme-secondary">{t('mgrDashboard.thisMonth', 'this month')}</span>}
        >
          <BarList
            data={leaderboard}
            onSelect={(key) => {
              const tech = technicians.find((tc) => (tc.user_id ?? tc.email ?? techName(tc)) === key);
              if (tech) onTechClick(tech);
            }}
            emptyText={t('mgrDashboard.noTechs', 'No technician activity this month.')}
          />
        </Card>

        <Card title={t('mgrDashboard.cardIssues', 'Issues (30d)')} icon={<AlertTriangle size={16} />}>
          <div className="grid grid-cols-1 gap-2">
            <StatTile
              icon={<AlertTriangle size={15} />}
              label={t('mgrDashboard.tileTotalIssues', 'Total issues')}
              value={issues.total_issues_30d}
              tone="red"
              title={t(
                'mgrDashboard.tileOpenIssuesHint',
                'Every individual repair a technician flagged on a zone in the last 30 days. One zone can need more than one repair, and each counts separately. Zones checked clean ("No Repairs Needed") are not counted.',
              )}
            />
            <StatTile
              icon={<AlertTriangle size={15} />}
              label={t('mgrDashboard.tileControllerFailures', 'Controller failures')}
              value={issues.controller_failures_30d}
              tone="amber"
              title={t(
                'mgrDashboard.tileControllerFailuresHint',
                'Controllers with at least one problem found in the last 30 days. A controller checked clean ("No Repairs Needed") is not counted.',
              )}
            />
            <StatTile
              icon={<MapPin size={15} />}
              label={t('mgrDashboard.tilePropsWithIssues', 'Properties affected')}
              value={issues.properties_with_issues_30d}
              tone="gray"
              title={t(
                'mgrDashboard.tilePropsWithIssuesHint',
                'Properties where at least one zone repair or controller problem was found in the last 30 days.',
              )}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line pt-3">
            {/* Counts are open + in_progress, so the label says "unresolved" — the
                Feedback screen defaults to the same set, and the two reconcile. */}
            <Pill tone="red" icon={<Bug size={11} />}>
              {t('mgrDashboard.pillUnresolvedBugs', '{{n}} unresolved bugs', { n: summary.feedback.open_bugs })}
            </Pill>
            <Pill tone="violet" icon={<Sparkles size={11} />}>
              {t('mgrDashboard.pillUnresolvedFeatures', '{{n}} unresolved requests', { n: summary.feedback.open_features })}
            </Pill>
          </div>
        </Card>
      </div>
    </div>
  );
}

function TechModal({ tech, onClose }: { tech: TechRow; onClose: () => void }) {
  const { t } = useTranslation('common');
  // Pending = submitted that aren't yet approved (best-effort, never negative).
  const pending = Math.max(0, tech.submitted - tech.approved);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl border border-line bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
              <Users size={16} />
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold text-theme-primary">{techName(tech)}</h3>
              {tech.email && <p className="truncate text-xs text-theme-secondary">{tech.email}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-theme-secondary hover:bg-subtle"
            title={t('mgrDashboard.close', 'Close')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4">
          <p className="mb-3 text-xs font-medium uppercase tracking-wide text-theme-secondary">
            {t('mgrDashboard.thisMonth', 'this month')}
          </p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-subtle px-2 py-3">
              <div className="text-xl font-bold tabular-nums" style={{ color: '#3b82f6' }}>{tech.total}</div>
              <div className="text-[10px] uppercase tracking-wide text-theme-secondary">{t('mgrDashboard.techTotal', 'Total')}</div>
            </div>
            <div className="rounded-lg bg-subtle px-2 py-3">
              <div className="text-xl font-bold tabular-nums" style={{ color: '#f59e0b' }}>{tech.submitted}</div>
              <div className="text-[10px] uppercase tracking-wide text-theme-secondary">{t('mgrDashboard.techSubmitted', 'Submitted')}</div>
            </div>
            <div className="rounded-lg bg-subtle px-2 py-3">
              <div className="text-xl font-bold tabular-nums" style={{ color: '#10b981' }}>{tech.approved}</div>
              <div className="text-[10px] uppercase tracking-wide text-theme-secondary">{t('mgrDashboard.techApproved', 'Approved')}</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Pill tone="blue">{t('mgrDashboard.pillDraftsCount', '{{n}} in draft', { n: Math.max(0, tech.total - tech.submitted) })}</Pill>
            <Pill tone="amber">{t('mgrDashboard.pillAwaiting', '{{n}} awaiting review', { n: pending })}</Pill>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compliance tab
// ---------------------------------------------------------------------------

interface ComplianceRollup {
  counts: Record<ComplianceStatus, number>;
  byCity: Array<{ city: string; flagged: number; total: number; entry: CityScheduleEntry }>;
  total: number;
  evaluated: number;
  pct: number | null;
}

function ComplianceTab({
  rollup,
  loading,
  error,
  onCityClick,
}: {
  rollup: ComplianceRollup | null;
  loading: boolean;
  error: string | null;
  onCityClick: (entry: CityScheduleEntry) => void;
}) {
  const { t } = useTranslation('common');

  if (loading && !rollup) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-theme-secondary">
        <RefreshCw size={18} className="animate-spin" />
        {t('mgrDashboard.complianceLoading', 'Loading compliance…')}
      </div>
    );
  }
  if (error && !rollup) return <InlineError message={error} />;
  if (!rollup) return <p className="py-12 text-center text-sm text-theme-secondary">—</p>;

  const segments: DonutSegment[] = COMPLIANCE_ORDER.map((s) => ({
    key: s,
    label: t(COMPLIANCE_META[s].labelKey, COMPLIANCE_META[s].labelEn),
    value: rollup.counts[s],
    color: COMPLIANCE_META[s].color,
  })).filter((s) => s.value > 0);

  const cityBars: BarDatum[] = rollup.byCity
    .filter((c) => c.flagged > 0)
    .sort((a, b) => b.flagged - a.flagged)
    .map((c) => ({
      key: c.city,
      label: c.city,
      value: c.flagged,
      color: '#dc2626',
      sub: t('mgrDashboard.cityBarSub', 'of {{total}}', { total: c.total }),
    }));

  return (
    <div className="flex flex-col gap-4">
      {error && <InlineError message={error} />}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          label={t('mgrDashboard.complianceCompliant', 'Compliant')}
          value={rollup.counts.compliant}
          tone="emerald"
        />
        <StatTile
          label={t('mgrDashboard.complianceNonCompliant', 'Non-compliant')}
          value={rollup.counts.non_compliant}
          tone="red"
        />
        <StatTile
          label={t('mgrDashboard.complianceUndetermined', "Can't verify")}
          value={rollup.counts.undetermined}
          tone="amber"
        />
        <StatTile
          icon={<Droplets size={15} />}
          label={t('mgrDashboard.tileCompliance', 'Compliance')}
          value={rollup.pct == null ? '—' : `${rollup.pct}%`}
          tone="violet"
          sub={
            <Tooltip content={t('mgrDashboard.complianceRateHint', 'Compliant ÷ evaluable properties')}>
              <span className="cursor-help underline decoration-dotted">
                {t('mgrDashboard.evaluatedSub', '{{n}} evaluated', { n: rollup.evaluated })}
              </span>
            </Tooltip>
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={t('mgrDashboard.cardOrgCompliance', 'Org-wide compliance')} icon={<Droplets size={16} />}>
          <div className="flex items-center gap-4">
            <Donut
              segments={segments}
              centerValue={rollup.total}
              centerLabel={t('mgrDashboard.propertiesLabel', 'properties')}
            />
            <div className="flex-1 min-w-0">
              {segments.length === 0 ? (
                <p className="text-sm text-theme-secondary">{t('mgrDashboard.noCompliance', 'No compliance data.')}</p>
              ) : (
                <Legend segments={segments} total={rollup.total} />
              )}
            </div>
          </div>
          <p className="mt-3 border-t border-line pt-3 text-xs text-theme-secondary">
            {t(
              'mgrDashboard.complianceReportNote',
              'For per-property detail and what to fix, see the full Compliance Report.',
            )}
          </p>
        </Card>

        <Card
          title={t('mgrDashboard.cardByCity', 'Flagged by city')}
          icon={<MapPin size={16} />}
          action={<span className="text-xs text-theme-secondary">{t('mgrDashboard.flaggedHint', 'non-compliant + unverified')}</span>}
        >
          <BarList
            data={cityBars}
            onSelect={(key) => {
              const city = rollup.byCity.find((c) => c.city === key);
              if (city) onCityClick(city.entry);
            }}
            emptyText={t('mgrDashboard.noFlaggedCities', 'No cities have flagged properties.')}
          />
        </Card>
      </div>
    </div>
  );
}
